import React from 'react';
import { Document, Page, Text } from '@react-pdf/renderer';
import { ReportData } from '../types/report.types';
import { SectionPageProvider } from './SectionPageContext';
import { filterReportDataByRunId } from '../utils/findingsUtils';
import { registerReportFonts } from '../utils/reportSharedUtils';

// V2 Section Components
import CoverPageV2 from './sections/v2/CoverPageV2';
import ExecutiveSummaryPageV2 from './sections/v2/ExecutiveSummaryPageV2';
import ScopePageV2 from './sections/v2/ScopePageV2';
import MethodologyPageV2 from './sections/v2/MethodologyPageV2';
import OwaspTop10PageV2 from './sections/v2/OwaspTop10PageV2';
import SeverityRatingPageV2 from './sections/v2/SeverityRatingPageV2';
import FindingsSummaryPageV2 from './sections/v2/FindingsSummaryPageV2';
import RemediationRoadmapPageV2 from './sections/v2/RemediationRoadmapPageV2';
import ConclusionToolsPageV2 from './sections/v2/ConclusionToolsPageV2';
import AboutUsPageV2 from './sections/v2/AboutUsPageV2';

// Register fonts once globally
registerReportFonts();

interface ExecutiveSummaryTemplatePDF2Props {
  reportData: ReportData;
  pieChartImage?: string;
  barChartImage?: string;
  processing?: boolean;
}

const ExecutiveSummaryTemplatePDF2Content: React.FC<ExecutiveSummaryTemplatePDF2Props> = ({
  reportData: rawReportData,
  pieChartImage,
  barChartImage,
  processing,
}) => {
  const reportData = React.useMemo(() => filterReportDataByRunId(rawReportData) || rawReportData, [rawReportData]);

  if (processing) {
    return (
      <Document>
        <Page>
          <Text>Loading...</Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      {/* Cover Page */}
      <CoverPageV2 reportData={reportData} />

      {/* Executive Summary */}
      <ExecutiveSummaryPageV2
        reportData={reportData}
        pieChartImage={pieChartImage}
        barChartImage={barChartImage}
      />

      {/* Scope */}
      <ScopePageV2 reportData={reportData} />

      {/* Methodology */}
      <MethodologyPageV2 reportData={reportData} />

      {/* OWASP Top 10 */}
      <OwaspTop10PageV2 reportData={reportData} />

      {/* Severity Rating Definitions */}
      <SeverityRatingPageV2 reportData={reportData} />

      {/* Findings Summary (Charts + severity table) */}
      <FindingsSummaryPageV2
        reportData={reportData}
        pieChartImage={pieChartImage}
        barChartImage={barChartImage}
      />

      {/* Remediation Roadmap */}
      <RemediationRoadmapPageV2 reportData={reportData} />

      {/* Conclusion & Tools Approach */}
      <ConclusionToolsPageV2 reportData={reportData} />

      {/* About Us Page */}
      <AboutUsPageV2 />
    </Document>
  );
};

const ExecutiveSummaryTemplatePDF2: React.FC<ExecutiveSummaryTemplatePDF2Props> = (props) => (
  <SectionPageProvider>
    <ExecutiveSummaryTemplatePDF2Content {...props} />
  </SectionPageProvider>
);

export default ExecutiveSummaryTemplatePDF2;
